import React, { useMemo, useState } from 'react';
import { PRO_MAGENTA, MUTED, LISTED_GRAY } from './proUi';
import { AmberNote, fieldLabel, FLASH } from './setup/setupUi';
import { INK, INSET, CHIP } from '../club/clubUi';

/**
 * Pro setup · WHAT YOU DO, gated by lane (design_handoff_pro_setup, IDENTITY card).
 *
 * Instead of rendering all ~93 roles at once, the Pro first claims up to 3 lanes (craft groups),
 * then only those lanes' roles show. Lanes are a UI gate only — they are derived from the saved
 * crafts on load, never persisted; a returning Pro with roles lands straight in the role picker.
 *
 * Selected fill is PRO_MAGENTA (#FF00FF), the app's one Pro-identity magenta, matching every other
 * selected craft chip.
 *
 * Props:
 *  - groups: [{ key, label, crafts: [{ slug, label }] }] — the taxonomy (from /pro/crafts/groups).
 *  - value: string[] — selected craft slugs (max 6).
 *  - onToggleCraft(slug): toggles one craft (parent keeps the 6-cap and the craft→kind switch).
 *  - onDropCrafts(slugs): removes a whole lane's crafts at once (on lane deselect).
 *  - flash: the SAVE & CONTINUE refusal — a 3px #FF00AA ring round the block and a pill saying why.
 */
export const LANE_BLURBS = {
  'sound-show': 'DJs, bands, MCs, comedians, sound + lights',
  'food-drink': 'Caterers, chefs, bartenders, food trucks, carts',
  staff: 'Servers, security, door, setup + cleanup crews',
  'space-gear': 'Venues, tents, tables, power, dance floors, AV',
  'look-feel': 'Florists, decor, balloons, signage, props, graphic design',
  capture: 'Photo, video, photo booths, drones',
  'kids-wellness': 'Face paint, magic, bouncy castles, yoga, massage',
  logistics: 'Shuttles, valet, parking, waste, permits, promoters',
  planning: 'Planners, coordinators, day-of managers, stylists',
  places: 'Restaurants, cafés, bars, pubs, breweries, wineries',
  shops: 'Party supplies, liquor, costumes, gifts, records, more',
  other: 'Charities and anything that isn’t listed',
};

export function laneBlurb(group) {
  if (LANE_BLURBS[group.key]) return LANE_BLURBS[group.key];
  const labels = group.crafts.map((c) => c.label);
  return labels.length > 4 ? `${labels.slice(0, 4).join(', ')} + more` : labels.join(', ');
}

export function laneTooltip(group) {
  const n = group.crafts.length;
  return `${n} role${n === 1 ? '' : 's'}: ${group.crafts.map((c) => c.label).join(', ')}`;
}

const count = { fontSize: 10, fontWeight: 700, letterSpacing: '.12em', textTransform: 'uppercase' };

export default function LaneGate({ groups, value, onToggleCraft, onDropCrafts, mobile = false, flash = false }) {
  const laneOf = useMemo(() => {
    const m = {};
    groups.forEach((g) => g.crafts.forEach((c) => { m[c.slug] = g.key; }));
    return m;
  }, [groups]);
  const derivedLanes = useMemo(() => [...new Set(value.map((s) => laneOf[s]).filter(Boolean))], [value, laneOf]);

  const [phase, setPhase] = useState(value.length > 0 ? 'roles' : 'lanes');
  const [lanes, setLanes] = useState(derivedLanes.slice(0, 3));

  const laneFull = lanes.length >= 3;
  const roleFull = value.length >= 6;
  const claimed = groups.filter((g) => lanes.includes(g.key));

  const toggleLane = (key) => {
    if (lanes.includes(key)) {
      setLanes(lanes.filter((k) => k !== key));
      const drop = groups.find((g) => g.key === key)?.crafts.map((c) => c.slug) || [];
      const inLane = value.filter((s) => drop.includes(s));
      if (inLane.length) onDropCrafts(inLane);
    } else if (!laneFull) {
      setLanes([...lanes, key]);
    }
  };

  const frame = (children, testId) => (
    <div data-testid={testId} data-field="roles" className="flex flex-col font-mono" style={{ gap: 12, margin: -10, padding: 10, borderRadius: 24, boxShadow: flash ? `0 0 0 3px ${FLASH}` : '0 0 0 0 transparent', transition: 'box-shadow .2s' }}>
      {flash && <span className="self-start" style={{ padding: '5px 11px', borderRadius: 999, background: FLASH, color: '#fff', fontSize: 9, fontWeight: 800, letterSpacing: '.14em' }}>PICK AT LEAST ONE ROLE TO CONTINUE</span>}
      {children}
    </div>
  );

  /* ─────────────── Phase A · lanes ─────────────── */
  if (phase === 'lanes') {
    return frame((
      <>
        <div className="flex items-baseline justify-between" style={{ gap: 12 }}>
          <span style={fieldLabel}>WHAT LANE ARE YOU IN</span>
          <span style={{ ...count, color: LISTED_GRAY }} data-testid="lane-count">{lanes.length}/3 LANES</span>
        </div>
        <p className="m-0" style={{ fontSize: 12, lineHeight: 1.6, color: MUTED }}>Pick the lanes you work in. You choose your roles next — only these lanes will show.</p>
        <div className="grid" style={{ gridTemplateColumns: mobile ? 'minmax(0,1fr)' : 'repeat(auto-fill, minmax(210px, 1fr))', gap: 8 }}>
          {groups.map((g) => {
            const on = lanes.includes(g.key);
            const disabled = !on && laneFull;
            return (
              <button key={g.key} type="button" onClick={() => toggleLane(g.key)} disabled={disabled} aria-pressed={on} data-testid={`lane-${g.key}`}
                title={laneTooltip(g)}
                className="flex flex-col text-left font-mono transition-colors duration-200 disabled:cursor-not-allowed"
                style={{ gap: 6, padding: '14px 16px', border: 'none', borderRadius: 18, background: on ? PRO_MAGENTA : INSET, color: INK, opacity: disabled ? 0.4 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}>
                <span className="flex justify-between" style={{ gap: 8 }}>
                  <span style={{ fontSize: 11, fontWeight: 800, letterSpacing: '.12em', textTransform: 'uppercase' }}>{g.label}</span>
                  <span className="flex-none" style={{ fontSize: 9, letterSpacing: '.1em', opacity: 0.6, textTransform: 'uppercase' }}>{g.crafts.length} ROLE{g.crafts.length === 1 ? '' : 'S'}</span>
                </span>
                <span style={{ fontSize: 11, lineHeight: 1.45, opacity: 0.75 }} data-testid={`lane-blurb-${g.key}`}>{laneBlurb(g)}</span>
              </button>
            );
          })}
        </div>
        {lanes.length > 0 && value.length === 0 && <span style={{ fontSize: 11, fontWeight: 700, letterSpacing: '.1em', color: '#92400E' }}>NEXT · PICK YOUR ROLES BEFORE YOU CAN CONTINUE</span>}
        <button type="button" onClick={() => setPhase('roles')} disabled={lanes.length === 0} data-testid="pick-roles"
          className="self-start font-mono hover:bg-[#FF00AA] disabled:cursor-default"
          style={{ height: 46, padding: '0 22px', border: 'none', borderRadius: 999, background: INK, color: '#fff', opacity: lanes.length ? 1 : 0.35, fontSize: 11, fontWeight: 800, letterSpacing: '.14em', cursor: 'pointer', transition: 'background-color .2s' }}>PICK ROLES →</button>
      </>
    ), 'lane-picker');
  }

  /* ─────────────── Phase B · roles ─────────────── */
  return frame((
    <>
      <div className="flex items-baseline justify-between" style={{ gap: 12 }}>
        <span style={fieldLabel}>WHAT YOU DO</span>
        <span style={{ ...count, color: roleFull ? '#000' : LISTED_GRAY }} data-testid="crafts-count">{value.length}/6 PICKED</span>
      </div>

      <div className="flex flex-wrap items-center" style={{ gap: 8, padding: '8px 8px 8px 18px', borderRadius: mobile ? 22 : 999, background: INSET }} data-testid="lane-bar">
        <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.16em', color: MUTED }}>YOUR LANES</span>
        {claimed.map((g) => (
          <span key={g.key} style={{ padding: '7px 12px', borderRadius: 999, background: '#fff', fontSize: 10, fontWeight: 800, letterSpacing: '.12em', textTransform: 'uppercase' }}>{g.label}</span>
        ))}
        <button type="button" onClick={() => setPhase('lanes')} data-testid="change-lanes"
          className="ml-auto font-mono hover:bg-[#FF00AA]"
          style={{ height: 34, padding: '0 16px', border: 'none', borderRadius: 999, background: INK, color: '#fff', fontSize: 10, fontWeight: 800, letterSpacing: '.14em', cursor: 'pointer', transition: 'background-color .2s' }}>CHANGE</button>
      </div>

      {claimed.map((g) => (
        <div key={g.key} className="flex flex-col" style={{ gap: 10, paddingTop: 6 }}>
          <span style={{ fontSize: 9, fontWeight: 700, letterSpacing: '.16em', color: MUTED, textTransform: 'uppercase' }}>{g.label}</span>
          <div className="flex flex-wrap" style={{ gap: 6 }}>
            {g.crafts.map((c) => {
              const on = value.includes(c.slug);
              const disabled = !on && roleFull;
              return (
                <button key={c.slug} type="button" onClick={() => onToggleCraft(c.slug)} disabled={disabled} aria-pressed={on} data-testid={`craft-${c.slug}`}
                  className="font-mono transition-colors duration-200 disabled:cursor-not-allowed"
                  style={{ height: mobile ? 44 : 38, padding: '0 16px', border: 'none', borderRadius: 999, fontSize: 13, whiteSpace: 'nowrap', fontWeight: on ? 800 : 400, background: on ? PRO_MAGENTA : CHIP, color: INK, opacity: disabled ? 0.4 : 1, cursor: disabled ? 'not-allowed' : 'pointer' }}>{c.label}</button>
              );
            })}
          </div>
        </div>
      ))}

      {value.length === 0 && <AmberNote>Pick at least one role. Hosts find you by role — no role, no results.</AmberNote>}
      {roleFull && <span style={{ ...count, color: MUTED }}>6 IS THE MAX. DROP ONE TO ADD ANOTHER.</span>}
    </>
  ), 'role-picker');
}
